import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface SopRow {
  readonly sopId: string;
  readonly sopCode: string;
  readonly title: string;
  readonly sopType: string;
  readonly department: string;
  readonly status: string;
  readonly versionNumber: string;
  readonly isMandatory: boolean;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const SOP_TYPES = ['', 'OPERATIONAL', 'TECHNICAL', 'SAFETY', 'QUALITY', 'COMPLIANCE', 'FINANCIAL', 'ADMINISTRATIVE'];
const NEW_SOP_TYPES = ['OPERATIONAL', 'TECHNICAL', 'SAFETY', 'QUALITY', 'COMPLIANCE', 'FINANCIAL', 'ADMINISTRATIVE'];
const TERMINAL = ['PUBLISHED', 'RETIRED', 'OBSOLETE'];

/**
 * M35 — SOP Repository (§33). Register of Standard Operating Procedures (T464)
 * across the 7 SOP types, carrying the §8 governed lifecycle (DRAFT → PUBLISHED
 * → OBSOLETE), the owning department, the current version and whether the SOP
 * is mandatory (BR-01/BR-02 governed process). Filterable by SOP type, status,
 * department and free-text search. Tenant isolation is enforced server-side
 * (LAW 4).
 */
export function SopRepository(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<SopRow[]>([]);
  const [total, setTotal] = useState(0);
  const [sopType, setSopType] = useState('');
  const [status, setStatus] = useState('');
  const [department, setDepartment] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState(NEW_SOP_TYPES[0]);
  const [newDepartment, setNewDepartment] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (sopType) params.set('sopType', sopType);
    if (status) params.set('status', status);
    if (department) params.set('department', department);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: SopRow[]; total: number }>(`/v1/kms/sops${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, sopType, status, department, q]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m35.sop.titleRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/sops`, {
        method: 'POST',
        body: { title: newTitle.trim(), sopType: newType, department: newDepartment.trim() || undefined },
        token,
      });
      setNotice(t('m35.common.created', { name: newTitle.trim() }));
      setNewTitle('');
      setNewDepartment('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.sop.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: SopRow, op: string, label: string, perm: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/sops/${r.sopId}/${op}`, { method: 'POST', body, token });
      setNotice(t('m35.common.notice', { code: r.sopCode, label }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: SopRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/sops/${r.sopId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.sopCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.sop.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="sop-repository">
      <h1>{t('m35.sop.title')}</h1>

      <form aria-label="sop-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.common.title')}
          <input aria-label="field-sop-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
        </label>
        <label>
          {t('m35.common.type')}
          <select aria-label="field-sop-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {NEW_SOP_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.department')}
          <input aria-label="field-sop-department" value={newDepartment} onChange={(e) => setNewDepartment(e.target.value)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-sop" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.sop.createBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="sop-action-error">{actionError}</p>}
      {notice && <p aria-label="sop-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.type')}
          <select aria-label="filter-type" value={sopType} onChange={(e) => setSopType(e.target.value)}>
            {SOP_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.department')}
          <input aria-label="filter-department" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder={t('m35.common.department')} />
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.sop.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="sop-count">{t('m35.sop.count', { n: total })}</p>
          <DataTable<SopRow>
            ariaLabel="sop-table"
            testId="sop-table"
            rowKey={(r) => r.sopId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-type': r.sopType })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.sopCode },
              { key: 'title', header: t('m35.common.title'), render: (r) => r.title },
              { key: 'type', header: t('m35.common.type'), render: (r) => r.sopType },
              { key: 'department', header: t('m35.common.department'), render: (r) => r.department },
              { key: 'version', header: t('m35.common.version'), render: (r) => r.versionNumber },
              { key: 'mandatory', header: t('m35.sop.mandatory'), render: (r) => r.isMandatory ? t('m35.common.yes') : t('m35.common.no'), cellAriaLabel: (r) => `sop-mandatory-${r.sopCode}` },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'submit', t('m35.common.act.submitted'), 'M35_SOP_MANAGE')}>{t('m35.common.btnSubmit')}</Button>
                  )}
                  {r.status === 'IN_REVIEW' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'approve', t('m35.common.act.approved'), 'M35_SOP_APPROVE')}>{t('m35.common.btnApprove')}</Button>
                      <input aria-label={`reason-${r.sopCode}`} value={reasons[r.sopId] ?? ''} onChange={(e) => setReasons((m) => ({ ...m, [r.sopId]: e.target.value }))} placeholder={t('m35.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'reject', t('m35.common.act.rejected'), 'M35_SOP_REVIEW', { reason: reasons[r.sopId] ?? '' })}>{t('m35.common.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'publish', t('m35.common.act.published'), 'M35_SOP_PUBLISH')}>{t('m35.common.btnPublish')}</Button>
                  )}
                  {r.status === 'PUBLISHED' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`obsolete-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'obsolete', t('m35.common.act.obsolete'), 'M35_SOP_RETIRE')}>{t('m35.common.btnObsolete')}</Button>
                      <Button variant="danger" type="button" aria-label={`retire-${r.sopCode}`} disabled={busy} onClick={() => void act(r, 'retire', t('m35.common.act.retired'), 'M35_SOP_RETIRE')}>{t('m35.common.btnRetire')}</Button>
                    </>
                  )}
                  {!TERMINAL.includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.sopCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
